import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL, SOCKET_URL } from '../lib/utils';
import {
  type EventMapOverlay,
  type EventMapOverlaySavePayload,
  deleteEventMapOverlayApi,
  fetchEventMapOverlay,
  revokeOverlayImageUrl,
  saveEventMapOverlayApi,
} from '../lib/mapOverlay';
import type { EventMapOverlayMeta } from '../types';

interface UseEventMapOverlayOptions {
  eventId: string | null | undefined;
  token: string | null | undefined;
  /** When true, subscribe to socket updates (default true). */
  live?: boolean;
}

export function useEventMapOverlay({
  eventId,
  token,
  live = true,
}: UseEventMapOverlayOptions) {
  const [overlay, setOverlay] = useState<EventMapOverlay | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<EventMapOverlay | null>(null);
  overlayRef.current = overlay;

  const clearOverlay = useCallback(() => {
    revokeOverlayImageUrl(overlayRef.current);
    setOverlay(null);
  }, []);

  const reload = useCallback(async () => {
    if (!eventId || !token) {
      clearOverlay();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      revokeOverlayImageUrl(overlayRef.current);
      const next = await fetchEventMapOverlay(eventId, token);
      setOverlay(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load site map');
      setOverlay(null);
    } finally {
      setLoading(false);
    }
  }, [eventId, token, clearOverlay]);

  useEffect(() => {
    reload();
    return () => {
      revokeOverlayImageUrl(overlayRef.current);
    };
  }, [reload]);

  useEffect(() => {
    if (!live || !eventId || !token) return;

    const socket: Socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    const applyMetaOrReload = (nextMeta: EventMapOverlayMeta | null) => {
      if (!nextMeta?.imageFile) {
        clearOverlay();
        return;
      }
      const current = overlayRef.current;
      if (current?.imageFile && current.imageFile === nextMeta.imageFile) {
        setOverlay({ ...current, ...nextMeta, imageUrl: current.imageUrl });
        return;
      }
      reload();
    };

    const onOverlayUpdated = (payload: { eventId: string; mapOverlay: EventMapOverlayMeta | null }) => {
      if (payload.eventId !== eventId) return;
      applyMetaOrReload(payload.mapOverlay);
    };

    socket.on('event:map-overlay-updated', onOverlayUpdated);
    socket.on('event:updated', (event: { id: string; mapOverlay?: EventMapOverlayMeta | null }) => {
      if (event.id !== eventId) return;
      if (!Object.prototype.hasOwnProperty.call(event, 'mapOverlay')) return;
      applyMetaOrReload(event.mapOverlay ?? null);
    });

    return () => {
      socket.off('event:map-overlay-updated', onOverlayUpdated);
      socket.disconnect();
    };
  }, [live, eventId, token, reload]);

  const saveOverlay = useCallback(
    async (payload: EventMapOverlaySavePayload) => {
      if (!eventId || !token) return null;
      setSaving(true);
      setError(null);
      try {
        const saved = await saveEventMapOverlayApi(eventId, token, payload);
        await reload();
        return saved;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save site map';
        setError(message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [eventId, token, reload]
  );

  const removeOverlay = useCallback(async () => {
    if (!eventId || !token) return;
    setSaving(true);
    setError(null);
    try {
      await deleteEventMapOverlayApi(eventId, token);
      clearOverlay();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove site map';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [eventId, token, clearOverlay]);

  return {
    overlay,
    loading,
    saving,
    error,
    reload,
    saveOverlay,
    removeOverlay,
    setOverlayLocal: setOverlay,
  };
}
